import {
  parseTemplateDutyPointCode,
  type ArrangementType,
  type TemplateDecisionClass,
  type TemplateDecisionRowDto,
} from '@pams/shared';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [P7B.1] LINKS INTO THE TEMPLATE PAGES AND THE TYPED LIST — built in one place, because several
 *  pages build each of them (the library, the post type master, the record page, the sidebar) and a
 *  link must open the same screen from all of them.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

/**
 * The decisions page on one class, with one code's form open when `code` is given — the library's
 * "Decide" and the way back from the post type master. ⚠️ `URLSearchParams`, never a template string:
 * real codes carry `&`, `#`, `|` and `/`.
 */
export const templateDecisionHref = (
  setKey: string,
  decisionClass: TemplateDecisionClass,
  code: string | null = null,
): string => {
  const params = new URLSearchParams({ class: decisionClass });
  if (code !== null) params.set('code', code);
  return `/templates/${setKey}/decisions?${params.toString()}`;
};

/** What a link into a template highlights: one of its duty points, by key, or one of its printed totals, by id. */
export type TemplateTarget = { readonly point: string } | { readonly check: string };

/**
 * [TEMPLATE-FIND] A template BY ITS NUMBER, through the library's own `?t=` entry — `LibraryIndex` resolves
 * the number and keeps every other parameter, so no page needs a template id or the 182-row list to link
 * one. ⚠️ `URLSearchParams`, never a template string: a printed total's id holds `:` and `#` (`p78:t36#2`).
 * With no target it is byte-identical to the record page's `/templates/<set>?t=t12`.
 */
export const templateHref = (setKey: string, templateKey: string, target?: TemplateTarget): string => {
  const params = new URLSearchParams({ t: templateKey });
  if (target !== undefined) {
    if ('point' in target) params.set('point', target.point);
    else params.set('check', target.check);
  }
  return `/templates/${setKey}?${params.toString()}`;
};

/**
 * [TEMPLATE-FIND] Where a decision row's "Show it in template …" goes: a printed total's own check, or a duty
 * point's own row — `null` for every class that is not about one place in one template. The duty point is
 * split out of the CODE by the shared parser, never out of the label.
 */
export const decisionInTemplateHref = (
  setKey: string,
  row: Pick<TemplateDecisionRowDto, 'class' | 'code' | 'context'>,
): string | null => {
  if (row.context.class === 'TOTALS') {
    return row.context.templateKey === '' ? null : templateHref(setKey, row.context.templateKey, { check: row.code });
  }
  if (row.class === 'DUTY_POINT') {
    const parts = parseTemplateDutyPointCode(row.code);
    return parts === null ? null : templateHref(setKey, parts.templateKey, { point: parts.dutyPointKey });
  }
  return null;
};

/** [P7B.1 §4] A registered type's own list — its sidebar row, out of Manage Arrangements' tab. */
export const typedListHref = (type: ArrangementType): string => `/pa/${type}`;
